import type { ReactElement, ReactNode } from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';

export interface IconButtonProps extends Omit<AriaButtonProps, 'className' | 'style' | 'children' | 'aria-label'> {
  /** Accessible name; required because the button shows only an icon. */
  label: string;
  /** The surface the button sits on, so the icon never matches its background. */
  surface?: 'dark' | 'light';
  children: ReactNode;
  /** Layout utilities only. */
  className?: string;
}

const SURFACES = {
  dark: 'text-on-nav-strong hover:bg-on-nav-hover',
  light: 'text-ink hover:bg-panel'
} as const;

export function IconButton({
  label,
  surface = 'light',
  children,
  className,
  ...rest
}: IconButtonProps): ReactElement {
  // text-control: a text glyph child (the × in Banner and Drawer) drew at the browser's 10pt button font.
  return (
    <AriaButton
      {...rest}
      aria-label={label}
      className={`inline-flex items-center justify-center m-0 p-1 touch:min-w-[44px] touch:min-h-[44px] border border-solid border-transparent bg-transparent rounded-control font-ui text-control leading-control appearance-none cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink data-[disabled]:opacity-50 data-[disabled]:cursor-default ${SURFACES[surface]} ${className ?? ''}`.trim()}
    >
      {children}
    </AriaButton>
  );
}
